/**
 * Timezone-aware calendar helpers. All service rules are evaluated in the
 * agency's configured IANA timezone, never in the server's local zone.
 */

export interface LocalParts {
  /** YYYY-MM-DD in the agency timezone */
  date: string;
  year: number;
  month: number;
  day: number;
  /** 0 = Sunday … 6 = Saturday */
  weekday: number;
  hour: number;
  minute: number;
  /** minutes since local midnight */
  minuteOfDay: number;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const fmtCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = fmtCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hourCycle: 'h23',
    });
    fmtCache.set(timeZone, f);
  }
  return f;
}

export function localParts(at: Date, timeZone: string): LocalParts {
  const parts: Record<string, string> = {};
  for (const p of formatter(timeZone).formatToParts(at)) parts[p.type] = p.value;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  const hour = Number(parts.hour) % 24;
  const minute = Number(parts.minute);
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    year,
    month,
    day,
    weekday: WEEKDAYS[parts.weekday ?? 'Sun'] ?? 0,
    hour,
    minute,
    minuteOfDay: hour * 60 + minute,
  };
}

/** Days since epoch for a YYYY-MM-DD calendar date (timezone-free arithmetic). */
export function dayNumber(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** Number of whole calendar days strictly between two local dates. */
export function fullDaysBetween(earlierDate: string, laterDate: string): number {
  return dayNumber(laterDate) - dayNumber(earlierDate) - 1;
}

/** "HH:MM" → minutes since midnight. */
export function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

export function minutesToHhmm(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function addDaysIso(isoDate: string, days: number): string {
  const n = dayNumber(isoDate) + days;
  return new Date(n * 86_400_000).toISOString().slice(0, 10);
}

/** Calendar days from `fromDate` until `toDate` (negative if past). */
export function daysUntil(fromDate: string, toDate: string): number {
  return dayNumber(toDate) - dayNumber(fromDate);
}

export const HOUR_MS = 3_600_000;

/** Local wall-clock date + time in a time zone → the UTC instant (handles daylight saving). */
export function zonedToUtc(isoDate: string, hhmm: string, timeZone: string): Date {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const [hh, mm] = hhmm.split(':').map(Number) as [number, number];
  const want = Date.UTC(y, m - 1, d, hh, mm);
  let t = want;
  for (let i = 0; i < 3; i++) {
    const lp = localParts(new Date(t), timeZone);
    const got = Date.UTC(lp.year, lp.month - 1, lp.day, lp.hour, lp.minute);
    if (got === want) break;
    t += want - got;
  }
  return new Date(t);
}
